var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Component, Watch } from 'vue-property-decorator';
import DataTable from '@/components/data-table/DataTable';
import PageHeader from '@/components/page-header/PageHeader';
import { User } from '@/models/User';
import { Student } from '@/models/Student';
import { Teacher } from '@/models/Teacher';
import { Guardian } from '@/models/Guardian';
import { Supervisor } from '@/models/Supervisor';
import { Specialist } from '@/models/Specialist';
import { Reseller } from '@/models/Reseller';
import { Administrator } from '@/models/Administrator';
import { Root } from '@/models/Root';
import { avatarHtml } from '@/support/String';
import CreateStudentDialog from '@/views/Users/Student/CreateDialog';
import CreateGuardianDialog from '@/views/Users/Guardian/CreateDialog';
import CreateTeacherDialog from '@/views/Users/Teacher/CreateDialog';
import CreateSpecialistDialog from '@/views/Users/Specialist/CreateDialog';
import CreateSupervisorDialog from '@/views/Users/Supervisor/CreateDialog';
import CreateResellerDialog from '@/views/Users/Reseller/CreateDialog';
import CreateAdministratorDialog from '@/views/Users/Administrator/CreateDialog';
import CreateRootDialog from '@/views/Users/Root/CreateDialog';
import ErrorHandler from '@/support/ErrorHandler';
import CreateIdpDialog from '@/components/curriculum/CreateIdpDialog';
import { Group } from '@/models/Group';
import { GroupTypeEnum } from '@/models/GroupType';
import AsyncSelect from '@/components/async-select/AsyncSelect';
import { excludeRoles } from '@/support/ExcludeRoles';
import CreateDialog from '@/views/Groups/CreateDialog';
import AbstractUrl from '@/support/AbstractUrl';
import { RolesEnum } from '@/items/Roles';
import AddSkillsetDialog from '@/components/add-skillset-dialog/AddSkillsetDialog.vue';
import client from '@/support/Client';
let Users = class Users extends AbstractUrl {
    constructor() {
        super(...arguments);
        this.$pageTitle = 'Users';
        this.initiated = false;
        this.selected = [];
        this.selectedGroup = null;
        this.groupModel = new Group().sort('name');
        this.selectedUser = null;
        this.clearSelects = false;
        this.isCreating = false;
        this.isShowingDialog = false;
        this.isShowingManyDialog = false;
        this.isShowingCreatingIdp = false;
        this.isShowingCreatingGroup = false;
        this.isShowingSkillDialog = false;
        this.isConfirmingDelete = false;
        this.showDeletedStudents = false;
        this.type = null;
    }
    get groupSelectFilters() {
        return { exclude_types: [GroupTypeEnum.PROJECT] };
    }
    get tableOptions() {
        const sourceMap = {
            Student: {
                name: {
                    singular: this.$tc('roles.student', 1),
                    plural: this.$tc('roles.student', 2),
                },
                breadcrumbs: [{ name: this.$tc('roles.user', 2) }, { name: this.$tc('roles.student', 2), path: this.$route.path }],
                sort: {
                    key: 'name',
                    order: 'ASC',
                },
                headers: [
                    {
                        text: '',
                        value: 'avatar',
                        transform: avatarHtml,
                    },
                    {
                        text: this.$t('name.title'),
                        value: 'name',
                        action: this.showDeletedStudents ? undefined : '/students/{id}',
                        sortable: {
                            key: 'name',
                            order: 'ASC',
                        },
                    },
                    { text: this.$t('age.title'), value: 'age' },
                ],
                actions: !this.exclude ? [
                    ...!this.showDeletedStudents ? [{
                            type: 'view agenda',
                            label: 'view agenda',
                            icon: 'view_agenda',
                            action: '/students/{id}/roster',
                        }] : [],
                    ...!this.showDeletedStudents ? [{
                            type: 'view',
                            label: 'view',
                            icon: 'remove_red_eye',
                            action: '/students/{id}',
                        }] : [],
                    ...!this.showDeletedStudents ? [{
                            type: 'edit',
                            label: 'edit',
                            icon: 'edit',
                            action: '/students/{id}/profile',
                            roles: ['teacher'],
                        }] : [],
                    ...!this.showDeletedStudents ? [{
                            type: 'delete',
                            label: 'delete',
                            icon: 'delete',
                            action: (vm, user) => {
                                this.selectedUser = user;
                                this.$store.dispatch('openDialog', this.dialogOptions);
                            },
                            roles: [RolesEnum.ADMIN, RolesEnum.ROOT],
                        }] : [],
                    ...this.showDeletedStudents ? [{
                            type: 'restore user',
                            text: this.$tc('user.restore'),
                            icon: 'unarchive',
                            action: (vm, user) => {
                                this.restoreStudent(user);
                            },
                            roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                        }] : [],
                ] : [
                    {
                        type: 'view',
                        label: 'view',
                        icon: 'remove_red_eye',
                        action: '/students/{id}',
                    },
                ],
                dropdown: [
                    {
                        text: this.$tc('actions.delete', 1) + ' ' + this.$tc('roles.student', 2),
                        icon: 'delete',
                        action: (vm) => {
                            this.isShowingManyDialog = true;
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                filters: [],
                model: new Student().filter({
                    ...this.selectedGroup ? { group: this.selectedGroup.value } : {},
                    ...this.showDeletedStudents ? { only_trashed: 1 } : {},
                }),
            },
            Teacher: {
                name: {
                    singular: this.$tc('roles.teacher', 1),
                    plural: this.$tc('roles.teacher', 2),
                },
                breadcrumbs: [{ name: this.$tc('roles.user', 2) }, { name: this.$tc('roles.teacher', 2), path: this.$route.path }],
                sort: {
                    key: 'name',
                    order: 'ASC',
                },
                headers: [
                    {
                        text: '',
                        value: 'avatar',
                        transform: avatarHtml,
                    },
                    {
                        text: this.$t('name.title'),
                        value: 'name',
                        action: '/teachers/{id}',
                        sortable: {
                            key: 'name',
                            order: 'ASC',
                        },
                    },
                ],
                actions: [
                    {
                        type: 'view',
                        label: 'view',
                        icon: 'remove_red_eye',
                        action: '/teachers/{id}',
                    },
                    {
                        type: 'edit',
                        label: 'edit',
                        icon: 'edit',
                        action: '/teachers/{id}',
                    },
                    {
                        type: 'delete',
                        label: 'delete',
                        icon: 'delete',
                        action: (vm, user) => {
                            this.selectedUser = user;
                            this.$store.dispatch('openDialog', this.dialogOptions);
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                dropdown: [
                    {
                        text: this.$tc('actions.delete', 1) + ' ' + this.$tc('roles.teacher', 2),
                        icon: 'delete',
                        action: (vm) => {
                            this.isShowingManyDialog = true;
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                filters: [],
                model: new Teacher(),
            },
            Guardian: {
                name: {
                    singular: this.$tc('roles.guardian', 1),
                    plural: this.$tc('roles.guardian', 2),
                },
                breadcrumbs: [{ name: this.$tc('roles.user', 2) }, { name: this.$tc('roles.guardian', 2), path: this.$route.path }],
                sort: {
                    key: 'name',
                    order: 'ASC',
                },
                headers: [
                    {
                        text: '',
                        value: 'avatar',
                        transform: avatarHtml,
                    },
                    {
                        text: this.$t('name.title'),
                        value: 'name',
                        action: '/guardians/{id}',
                        sortable: {
                            key: 'name',
                            order: 'ASC',
                        },
                    },
                ],
                actions: [
                    {
                        type: 'view',
                        label: 'view',
                        icon: 'remove_red_eye',
                        action: '/guardians/{id}',
                    },
                    {
                        type: 'edit',
                        label: 'edit',
                        icon: 'edit',
                        action: '/guardians/{id}',
                    },
                    {
                        type: 'delete',
                        label: 'delete',
                        icon: 'delete',
                        action: (vm, user) => {
                            this.selectedUser = user;
                            this.$store.dispatch('openDialog', this.dialogOptions);
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                dropdown: [
                    {
                        text: this.$tc('actions.delete', 1) + ' ' + this.$tc('roles.guardian', 2),
                        icon: 'delete',
                        action: (vm) => {
                            this.isShowingManyDialog = true;
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                filters: [],
                model: new Guardian(),
            },
            Supervisor: {
                name: {
                    singular: this.$tc('roles.supervisor', 1),
                    plural: this.$tc('roles.supervisor', 2),
                },
                breadcrumbs: [{ name: this.$tc('roles.user', 2) }, { name: this.$tc('roles.supervisor', 2), path: this.$route.path }],
                sort: {
                    key: 'name',
                    order: 'ASC',
                },
                headers: [
                    {
                        text: '',
                        value: 'avatar',
                        transform: avatarHtml,
                    },
                    {
                        text: this.$t('name.title'),
                        value: 'name',
                        action: '/supervisors/{id}',
                        sortable: {
                            key: 'name',
                            order: 'ASC',
                        },
                    },
                ],
                actions: [
                    {
                        type: 'view',
                        label: 'view',
                        icon: 'remove_red_eye',
                        action: '/supervisors/{id}',
                    },
                    {
                        type: 'edit',
                        label: 'edit',
                        icon: 'edit',
                        action: '/supervisors/{id}',
                    },
                    {
                        type: 'delete',
                        label: 'delete',
                        icon: 'delete',
                        action: (vm, user) => {
                            this.selectedUser = user;
                            this.$store.dispatch('openDialog', this.dialogOptions);
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                dropdown: [
                    {
                        text: this.$tc('actions.delete', 1) + ' ' + this.$tc('roles.supervisor', 2),
                        icon: 'delete',
                        action: (vm) => {
                            this.isShowingManyDialog = true;
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                filters: [],
                model: new Supervisor(),
            },
            Specialist: {
                name: {
                    singular: this.$tc('roles.specialist', 1),
                    plural: this.$tc('roles.specialist', 2),
                },
                breadcrumbs: [{ name: this.$tc('roles.user', 2) }, { name: this.$tc('roles.specialist', 2), path: this.$route.path }],
                sort: {
                    key: 'name',
                    order: 'ASC',
                },
                headers: [
                    {
                        text: '',
                        value: 'avatar',
                        transform: avatarHtml,
                    },
                    {
                        text: this.$t('name.title'),
                        value: 'name',
                        action: '/specialists/{id}',
                        sortable: {
                            key: 'name',
                            order: 'ASC',
                        },
                    },
                ],
                actions: [
                    {
                        type: 'view',
                        label: 'view',
                        icon: 'remove_red_eye',
                        action: '/specialists/{id}',
                    },
                    {
                        type: 'edit',
                        label: 'edit',
                        icon: 'edit',
                        action: '/specialists/{id}',
                    },
                    {
                        type: 'delete',
                        label: 'delete',
                        icon: 'delete',
                        action: (vm, user) => {
                            this.selectedUser = user;
                            this.$store.dispatch('openDialog', this.dialogOptions);
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                dropdown: [
                    {
                        text: this.$tc('actions.delete', 1) + ' ' + this.$tc('roles.specialist', 2),
                        icon: 'delete',
                        action: (vm) => {
                            this.isShowingManyDialog = true;
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                filters: [],
                model: new Specialist(),
            },
            Reseller: {
                name: {
                    singular: this.$tc('roles.reseller', 1),
                    plural: this.$tc('roles.reseller', 2),
                },
                breadcrumbs: [{ name: this.$tc('roles.user', 2) }, { name: this.$tc('roles.reseller', 2), path: this.$route.path }],
                sort: {
                    key: 'name',
                    order: 'ASC',
                },
                headers: [
                    {
                        text: '',
                        value: 'avatar',
                        transform: avatarHtml,
                    },
                    {
                        text: this.$t('name.title'),
                        value: 'name',
                        action: '/resellers/{id}',
                        sortable: {
                            key: 'name',
                            order: 'ASC',
                        },
                    },
                ],
                actions: [
                    {
                        type: 'view',
                        label: 'view',
                        icon: 'remove_red_eye',
                        action: '/resellers/{id}',
                    },
                    {
                        type: 'edit',
                        label: 'edit',
                        icon: 'edit',
                        action: '/resellers/{id}',
                    },
                    {
                        type: 'delete',
                        label: 'delete',
                        icon: 'delete',
                        action: (vm, user) => {
                            this.selectedUser = user;
                            this.$store.dispatch('openDialog', this.dialogOptions);
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                dropdown: [
                    {
                        text: this.$tc('actions.delete', 1) + ' ' + this.$tc('roles.reseller', 2),
                        icon: 'delete',
                        action: (vm) => {
                            this.isShowingManyDialog = true;
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                filters: [],
                model: new Reseller(),
            },
            Administrator: {
                name: {
                    singular: this.$tc('roles.administrator', 1),
                    plural: this.$tc('roles.administrator', 2),
                },
                breadcrumbs: [{ name: this.$tc('roles.user', 2) }, { name: this.$tc('roles.administrator', 2), path: this.$route.path }],
                sort: {
                    key: 'name',
                    order: 'ASC',
                },
                headers: [
                    {
                        text: '',
                        value: 'avatar',
                        transform: avatarHtml,
                    },
                    {
                        text: this.$t('name.title'),
                        value: 'name',
                        action: '/administrators/{id}',
                        sortable: {
                            key: 'name',
                            order: 'ASC',
                        },
                    },
                ],
                actions: [
                    {
                        type: 'view',
                        label: 'view',
                        icon: 'remove_red_eye',
                        action: '/administrators/{id}',
                    },
                    {
                        type: 'edit',
                        label: 'edit',
                        icon: 'edit',
                        action: '/administrators/{id}',
                    },
                    {
                        type: 'delete',
                        label: 'delete',
                        icon: 'delete',
                        action: (vm, user) => {
                            this.selectedUser = user;
                            this.$store.dispatch('openDialog', this.dialogOptions);
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                dropdown: [
                    {
                        text: this.$tc('actions.delete', 1) + ' ' + this.$tc('roles.administrator', 2),
                        icon: 'delete',
                        action: (vm) => {
                            this.isShowingManyDialog = true;
                        },
                        roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.RESELLER],
                    },
                ],
                filters: [],
                model: new Administrator().filter({ is_global: 0 }),
            },
            Root: {
                name: {
                    singular: this.$tc('roles.root', 1),
                    plural: this.$tc('roles.root', 2),
                },
                breadcrumbs: [{ name: this.$tc('roles.user', 2) }, { name: this.$tc('roles.root', 2), path: this.$route.path }],
                sort: {
                    key: 'name',
                    order: 'ASC',
                },
                headers: [
                    {
                        text: '',
                        value: 'avatar',
                        transform: avatarHtml,
                    },
                    {
                        text: this.$t('name.title'),
                        value: 'name',
                        action: '/admin/super-administrators/{id}',
                        sortable: {
                            key: 'name',
                            order: 'ASC',
                        },
                    },
                ],
                actions: [
                    {
                        type: 'view',
                        label: 'view',
                        icon: 'remove_red_eye',
                        action: '/admin/super-administrators/{id}',
                    },
                    {
                        type: 'edit',
                        label: 'edit',
                        icon: 'edit',
                        action: '/admin/super-administrators/{id}',
                    },
                    {
                        type: 'delete',
                        label: 'delete',
                        icon: 'delete',
                        action: (vm, user) => {
                            this.selectedUser = user;
                            this.$store.dispatch('openDialog', this.dialogOptions);
                        },
                        roles: [RolesEnum.ROOT, RolesEnum.ADMIN, RolesEnum.RESELLER],
                    },
                ],
                filters: [],
                model: new Root(),
            },
        };
        return sourceMap[this.type];
    }
    get dialogOptionsMany() {
        return {
            title: this.$t('dialogOptions.confirmation').toString(),
            text: `${this.$tc('dialogOptions.delete', 2, { item: this.selected.length + ' ' + this.$tc(`roles.${this.type ? this.type.toLowerCase() : 'user'}`, 2) }).toString()}`,
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.delete').toString(),
                    action: () => {
                        if (this.selected.length <= 0) {
                            return;
                        }
                        let counter = 0;
                        this.selected.forEach((user) => {
                            user
                                .delete()
                                .then((response) => {
                                if (response) {
                                    counter += 1;
                                    if (counter >= this.selected.length) {
                                        this.selected = [];
                                        this.refreshDataTable();
                                    }
                                }
                            })
                                .catch((error) => {
                                ErrorHandler.network(error);
                            });
                        });
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                    action: () => { },
                },
            },
        };
    }
    get dialogOptions() {
        const user = this.selectedUser;
        return {
            title: user.name || this.$t('dialogOptions.confirmation').toString(),
            text: this.$t('dialogOptions.deletePermanently', { item: this.$t(`dialogOptions.item.${user.filters.role}`) }).toString(),
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.delete').toString(),
                    action: () => {
                        if (!user) {
                            return;
                        }
                        user
                            .delete()
                            .then((response) => {
                            if (response) {
                                this.selectedUser = null;
                                this.refreshDataTable();
                            }
                        })
                            .catch((error) => {
                            ErrorHandler.network(error);
                        });
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                    action: () => {
                        this.selectedUser = null;
                    },
                },
            },
        };
    }
    get dropdown() {
        return this.tableOptions.dropdown;
    }
    get user() {
        return (this.$store.state.Auth ||
            new User({
                name: 'Undefined',
            }));
    }
    get asyncAppendIcon() {
        if (this.selectedGroup) {
            return 'close';
        }
        return 'filter_list';
    }
    get exclude() {
        return excludeRoles[this.$store.state.version].scoolUsers.table.actions.includes(this.$store.state.Auth.roles[0].id);
    }
    get isAdminPage() {
        return this.$route.matched.some((route) => route.name === 'admin');
    }
    routeChanged(to, from) {
        if (to.meta.type !== from.meta.type) {
            this.type = to.meta.type;
        }
    }
    mounted() {
        this.type = this.$route.meta && this.$route.meta.type ? this.$route.meta.type : null;
        this.$pageTitle = this.tableOptions.name.plural;
        if (this.$route.query && this.$route.query.group) {
            this.setGroupSelect();
        }
        else {
            this.initiated = true;
        }
    }
    onAddSkillCategory(category) {
        this.addSkillCategory(category);
    }
    async addSkillCategory(category) {
        try {
            await client('post', 'portfolios/skills/to-group-from-category', {
                category: category.id,
                users: this.selected.map((user) => user.id),
            });
            this.$store.dispatch('openDialog', this.addSkillSetSuccess);
        }
        catch (error) {
            //
        }
    }
    createPlayload(items) {
        return items.map((item) => {
            return `${item.id}`;
        });
    }
    getChildComponentByName(componentName) {
        return this.$children.find((child) => {
            return child.$options.name === componentName;
        });
    }
    userCreated() {
        this.isCreating = false;
        this.refreshDataTable();
    }
    clear() {
        this.groupModel = new Group().sort('name', 'DESC');
        this.clearSelects = true;
    }
    selectedItemsChange(value, oldValue) { }
    refreshDataTable() {
        document.dispatchEvent(new CustomEvent('datatable:hook', {
            detail: (dataTable) => {
                dataTable.refresh();
            },
        }));
    }
    canViewListItem(item) {
        // console.log('item', item.roles);
        // console.log('TEST', this.$store.state.Auth.hasRole(item.roles));
        if (!item.roles) {
            return false;
        }
        return this.$store.state.Auth.hasRole(item.roles);
    }
    async setGroupSelect() {
        try {
            const group = await new Group().find(this.$route.query.group);
            if (group && this.$refs.groupSelect) {
                this.$refs.groupSelect.setDefaultValue({
                    value: group.id,
                    text: group.name,
                });
            }
            this.$nextTick(() => {
                this.initiated = true;
            });
        }
        catch (error) { }
    }
    toggleDeletedStudents() {
        if (this.showDeletedStudents) {
            this.showDeletedStudents = false;
            return;
        }
        this.showDeletedStudents = true;
    }
    openCreateSkillDialog() {
        this.isShowingSkillDialog = true;
    }
    get addSkillSetSuccess() {
        return {
            title: 'Vaardighedenset succesvol toegevoegd',
            text: 'De geselecteerde vaardighedenset is succesvol toegevoegd aan de geselecteerde leerlingen',
            type: 'success',
            buttons: {
                confirm: {
                    text: 'Sluiten',
                },
            },
        };
    }
    async restoreStudent(student) {
        await student.restoreUser();
        this.refreshDataTable();
    }
    selectedGroupChanged(group, lastGoup) {
        if (!group) {
            this.clearQueryParams();
        }
        else {
            this.setQueryParams({ group: this.selectedGroup.value });
        }
    }
};
__decorate([
    Watch('$route'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", void 0)
], Users.prototype, "routeChanged", null);
__decorate([
    Watch('selected'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array, Array]),
    __metadata("design:returntype", void 0)
], Users.prototype, "selectedItemsChange", null);
__decorate([
    Watch('selectedGroup'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", void 0)
], Users.prototype, "selectedGroupChanged", null);
Users = __decorate([
    Component({
        components: {
            DataTable,
            PageHeader,
            'student-create-dialog': CreateStudentDialog,
            'guardian-create-dialog': CreateGuardianDialog,
            'teacher-create-dialog': CreateTeacherDialog,
            'specialist-create-dialog': CreateSpecialistDialog,
            'supervisor-create-dialog': CreateSupervisorDialog,
            'reseller-create-dialog': CreateResellerDialog,
            'administrator-create-dialog': CreateAdministratorDialog,
            'root-create-dialog': CreateRootDialog,
            CreateIdpDialog,
            AsyncSelect,
            'CreateGroupDialog': CreateDialog,
            AddSkillsetDialog,
        },
    })
], Users);
export default Users;
